import { EditorExtensionEnum, extensionsForSms, TipTapEditor } from '@systemeio/tip-tap';
import { FieldErrorAndDescription, FormInput, toast } from '@systemeio/ui';
import React, { useEffect } from 'react';
import { ConfirmationModalBeforeExit } from 'shared/confirmation-modal-before-exit/component';
import { RequestModuleEnum } from 'shared/enums/request-module-enum';
import { useLocoTranslation } from 'shared/hooks/use-loco-translation';
import { useRevalidateModules } from 'shared/hooks/use-revalidate-modules';
import { deepCompare } from 'shared/utils/deepCompare';
import { handle422Error } from 'shared/utils/parse-422-errors';
import { twJoin, twMerge } from 'tailwind-merge';
import { useSmsSubstitutes } from '../hooks/use-sms-substitutes';
import { useSmsTemplateActions } from '../hooks/use-sms-template-actions';
import { SenderTypeEnum, SmsTemplateCreateInterface, SmsTemplateId, SmsTemplateUpdateInterface } from '../types';
import { SmsSubstitutesBlock } from './sms-substitutes-block';
import { SmsTemplateRadioGroupSenderType } from 'modules/sms/components/sms-template-radio-group-sender-type';
const defaultErrors = {
  name: '',
  content: '',
  phoneNumber: '',
  alphanumericSender: '',
  messageServiceSid: ''
};
const defaultState = {
  name: '',
  senderType: SenderTypeEnum.PhoneNumber,
  phoneNumber: null,
  alphanumericSender: null,
  messageServiceSid: null,
  content: ''
};
export interface SmsTemplateFormProps {
  templateId?: SmsTemplateId;
  data?: SmsTemplateCreateInterface;
  inModal?: boolean;
  disabled?: boolean;
  onStatusChange?: ({
    isFetching,
    hasTwilioErrors
  }: {
    isFetching: boolean;
    hasTwilioErrors: boolean;
  }) => void;
  onSuccess?: (_: SmsTemplateUpdateInterface) => Promise<void>;
}
SmsTemplateForm.formId = 'sms-template-form';
export function SmsTemplateForm({
  templateId,
  data,
  inModal,
  disabled,
  onStatusChange,
  onSuccess
}: SmsTemplateFormProps) {
  const [isFetching, setIsFetching] = React.useState<boolean>(false);
  const [tempState, setTempState] = React.useState<SmsTemplateCreateInterface>(defaultState);
  const [errors, setErrors] = React.useState(defaultErrors);
  const [hasTwilioErrors, setHasTwilioErrors] = React.useState<boolean>(false);
  const {
    t
  } = useLocoTranslation();
  const {
    createSmsTemplate,
    updateSmsTemplate
  } = useSmsTemplateActions();
  const {
    revalidateModules
  } = useRevalidateModules();
  useEffect(() => {
    if (data) {
      setTempState({
        name: data.name,
        phoneNumber: data.phoneNumber,
        content: data.content,
        senderType: data.senderType,
        alphanumericSender: data.alphanumericSender,
        messageServiceSid: data.messageServiceSid
      });
    }
  }, [data]);
  useEffect(() => {
    if (onStatusChange) {
      onStatusChange({
        isFetching,
        hasTwilioErrors
      });
    }
  }, [isFetching, hasTwilioErrors]);
  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (isFetching) return;
    if (isPreFetching) return;
    if (disabled) return;
    setIsFetching(true);
    setErrors(defaultErrors);
    try {
      let response: SmsTemplateUpdateInterface;
      if (templateId) {
        response = await updateSmsTemplate({
          id: templateId,
          ...tempState
        });
        await revalidateModules([RequestModuleEnum.SmsTemplates]);
        await onSuccess?.(response);
        toast.success(t('dashboard.sms.sms_template_successfully_updated'));
      } else {
        response = await createSmsTemplate({
          ...tempState
        });
        await revalidateModules([RequestModuleEnum.SmsTemplates]);
        await onSuccess?.(response);
        toast.success(t('dashboard.sms.sms_template_successfully_created'));
      }
    } catch (error) {
      handle422Error(error, setErrors);
    } finally {
      setIsFetching(false);
    }
  };
  const isDataBeingChanged = React.useMemo(() => {
    if (!templateId) {
      return !deepCompare(defaultState, tempState);
    }
    const dataToCompare: SmsTemplateCreateInterface | null = data ? {
      name: data.name,
      senderType: data.senderType,
      phoneNumber: data.phoneNumber,
      alphanumericSender: data.alphanumericSender,
      messageServiceSid: data.messageServiceSid,
      content: data.content
    } : null;
    return !deepCompare(dataToCompare, tempState);
  }, [templateId, data, tempState]);
  const {
    substitutionData
  } = useSmsSubstitutes();
  const isPreFetching = templateId ? !data : false;
  return <form id={SmsTemplateForm.formId} className={'flex flex-col gap-3'} onSubmit={handleSubmit}>
      <div className={'flex flex-col gap-5 lg:flex-row'}>
        <div className={`min-w-0 lg:h-auto lg:basis-3/4`}>
          <div className={twMerge('bg-background-secondary flex w-full flex-col gap-5 rounded-md p-5', inModal && 'p-0')}>
            <div className={`flex basis-full flex-col gap-1`}>
              <span className="text-sm font-medium">{t('dashboard.sms.body')}</span>
              <TipTapEditor mode="text" className={twJoin(errors.content && 'border-danger')} extensions={extensionsForSms} isPreFetching={isPreFetching} isEditable={!disabled && !isFetching} content={tempState.content} onChange={content => setTempState({
              ...tempState,
              content
            })} extensionOptions={{
              [EditorExtensionEnum.Substitution]: {
                substitutions: substitutionData
              }
            }} characterCountHint={t('dashboard.simple_mail_editor.character_count.tooltip')} />
              <FieldErrorAndDescription error={errors.content} />
            </div>
          </div>
        </div>
        <div className={twMerge(`static top-5 flex h-fit flex-col gap-5 sm:min-w-[320px] lg:sticky lg:basis-1/4`, inModal && 'lg:pt-6')}>
          <div className={twMerge('bg-background-secondary flex w-full flex-col gap-5 rounded-md p-5', inModal && 'border-text-secondary/30 rounded-lg border')}>
            <FormInput label={t('dashboard.sms.template_name')} value={tempState.name} isPreFetching={isPreFetching} disabled={disabled || isFetching} onChange={e_0 => setTempState({
            ...tempState,
            name: e_0.target.value
          })} required error={errors.name} />
          </div>

          <div className={twMerge('bg-background-secondary flex w-full flex-col gap-5 rounded-md p-5', inModal && 'border-text-secondary/30 rounded-lg border')}>
            <SmsTemplateRadioGroupSenderType errors={errors} isFetching={isFetching} isPreFetching={isPreFetching} values={{
            senderType: tempState.senderType,
            phoneNumber: tempState.phoneNumber,
            alphanumericSender: tempState.alphanumericSender,
            messageServiceSid: tempState.messageServiceSid
          }} onChange={updates => setTempState(prev => ({
            ...prev,
            ...updates
          }))} disabled={disabled} onTwilioHasErrors={setHasTwilioErrors} />
          </div>

          <SmsSubstitutesBlock inModal={inModal} />
        </div>
      </div>

      <ConfirmationModalBeforeExit confirmationText={t('dashboard.sms.confirmation_message_before_exit')} hideModal={!isDataBeingChanged || isFetching} />
    </form>;
}